import React, {useState} from 'react';
import {StatesButton, StatesButtonStates, ThreeDotsLoader, ButtonSize} from '@wix/wix-ui-tpa/cssVars';
import {classes} from './PayNowButton.st.css';
import {usePanorama, useFedopsLogger, useExperiments} from '@wix/yoshi-flow-editor';
import {usePaymentsApi} from '../../../../common/components/WithPaymentsApi/WithPaymentsApi';
import {useControllerProps} from '../../Widget/ControllerContext';
import {toMonitorError} from '../../../../domain/utils/errorMonitor.utils';
import {useLocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {SPECS} from '../../../../common/constants';
import {FedopsInteractions} from '../../constants';
import {PaymentFlowMonitorBreadcrumbs} from '../../../../types/paymentRequestApp.types';
import {AddPaymentInfo} from '../../../../domain/utils/analytics.utils';
import {useFunctionResultObservation} from '@wix/function-result-observation';
import {pay} from './pay';

export const PayNowButtonDataHook = 'pay-now-button';

export const PayNowButton = () => {
  const localeKeys = useLocaleKeys();
  const [payNowButtonState, setPayNowButtonState] = useState(StatesButtonStates.IDLE);
  const [payNowButtonDisabled, setPayNowButtonDisabled] = useState(false);
  const {paymentRequestStore, navigationStore} = useControllerProps();
  const {paymentRequest, sendClickOnPayNowButtonBIEvent} = paymentRequestStore;
  const {
    navigateToThankYouPage,
    trackEvent,
    navigateToLinkExpiredPage,
    navigateToPaymentErrorPage,
    navigateToExternalUrl,
  } = navigationStore;
  const {withObservation} = useFunctionResultObservation();
  const chargeOrderPaymentRequest = withObservation(paymentRequestStore, 'chargeOrderPaymentRequest');
  const {refreshPaymentRequestState} = navigationStore;
  const panorama = usePanorama();
  const fedops = useFedopsLogger();
  const {paymentsApi, activePaymentId} = usePaymentsApi();
  const {experiments} = useExperiments();
  const useChargeApi = experiments.enabled(SPECS.ecomChargeOrderPaymentRequestApi);

  const resetPayNowButtonState = () => {
    setPayNowButtonState(StatesButtonStates.IDLE);
    setPayNowButtonDisabled(false);
  };

  const isLinkStillValid = (): boolean => {
    const now = new Date().getTime();
    const expirationDateTimeStamp = paymentRequest?.expirationDateTimeStamp ?? now;
    if (expirationDateTimeStamp < now) {
      navigateToLinkExpiredPage();
      return false;
    }

    return true;
  };

  const validateAndSubmit = async () => {
    setPayNowButtonState(StatesButtonStates.IN_PROGRESS);
    setPayNowButtonDisabled(true);

    try {
      sendClickOnPayNowButtonBIEvent(activePaymentId);
      panorama.transaction(FedopsInteractions.ValidatePaymentInteraction).start();
      fedops.interactionStarted(FedopsInteractions.ValidatePaymentInteraction);
      const isStillValid = isLinkStillValid();
      const validation = await paymentsApi?.validate();
      panorama.transaction(FedopsInteractions.ValidatePaymentInteraction).finish();
      fedops.interactionEnded(FedopsInteractions.ValidatePaymentInteraction);

      if (!validation?.isValid || !isStillValid) {
        resetPayNowButtonState();
        return;
      }

      panorama.errorMonitor().addBreadcrumb({
        message: PaymentFlowMonitorBreadcrumbs.validation,
      });
      trackEvent(...AddPaymentInfo({activePaymentMethod: activePaymentId}));

      panorama.transaction(FedopsInteractions.Pay).start();
      fedops.interactionStarted(FedopsInteractions.Pay);
      const paymentResult = await pay({
        useChargeApi,
        paymentsApi,
        chargeOrderPaymentRequest,
        paymentGatewayOrderId: paymentRequest.paymentGatewayOrderId,
        refreshPaymentRequestState,
        navigateToPaymentErrorPage,
        navigateToExternalUrl,
        panorama,
      });
      panorama.transaction(FedopsInteractions.Pay).finish();
      fedops.interactionEnded(FedopsInteractions.Pay);

      panorama.errorMonitor().addBreadcrumb({message: PaymentFlowMonitorBreadcrumbs.paid});

      if (paymentResult.navigatedAway) {
        return;
      }

      if (!paymentResult.succeeded) {
        resetPayNowButtonState();
        return;
      }

      navigateToThankYouPage();
    } catch (e) {
      panorama.errorMonitor().reportError(...toMonitorError(e, PaymentFlowMonitorBreadcrumbs.error));
      try {
        await paymentsApi?.cancelPayment?.();
      } finally {
        resetPayNowButtonState();
      }
    }
  };

  return (
    <StatesButton
      className={classes.payNowButton}
      data-hook={PayNowButtonDataHook}
      disabled={payNowButtonDisabled}
      upgrade={true}
      size={ButtonSize.medium}
      onClick={() => {
        void validateAndSubmit();
      }}
      idleContent={localeKeys.checkout.payLinks.mainCta()}
      state={payNowButtonState}
      inProgressContent={<ThreeDotsLoader className={classes.threeDotButton} />}
      wrapContent
    />
  );
};
